import { useContext, useState } from "react";
import { defaultFirmaAltFaktureringsAdresserColumns } from "../../../../../config/firmaConfig";
import useActiveColumns from "../../../../../hooks/useActiveColumns";
import {
  createColumnEditorTrigger,
  getHeaderActiveColumns,
  TD,
} from "../../../../../util/table";
import ActiveIcon from "../../../../icons/ActiveIcon";
import { ModalProvider } from "../../../../Modal/Modal";
import {
  TableBody,
  TableHead,
  TableRoot,
  TableRow,
} from "../../../../Table/TableBasic";
import IngenRegistreringer from "../../../person/id/IngenRegistreringer";
import OpretNyButtonBlock from "../../../person/id/OpretNyButtonBlock";
import ColumnEditorModal from "@/components/column-editor/ColumnEditorModal";
import { firmaAltAdresseForm } from "../../../../../config/form/firma/firmaAltAdresseFormConfig";
import { useQuery } from "@apollo/client";
import { GET_FIRMA_BY_ID_BETALINGSADRESSE } from "../../../../../api/firma/queries";
import { FirmaBetalingsadresse } from "../../../../../interfaces/schema";
import { SortBy } from "../../../../../interfaces/table";
import { AccordionCard } from "../../../../accordion/Accordion";
import { UserQueryContext } from "../../../../../context/UserContext";
import Spinner from "../../../../spinner/Spinner";
import { periodeIsActive } from "../../../../../util/date";
import ButtonDownload from "../../../../ButtonDownload";
import { tableToXLSX } from "../../../../../util/tableExportUtils";
import { orderBy } from "../../../../../util/orderBy";

export default function FirmaAltFaktureringsAdresser({
  firmaId,
}: {
  firmaId: string;
}) {
  const { user, hasPermission } = useContext(UserQueryContext);
  const canWrite = hasPermission(user, "BasisWrite");
  const [sortBy, setSortBy] = useState<SortBy[]>(defaultSortBy);
  const [
    firmaAltFaktureringsAdresser,
    setFirmaAltFaktureringsAdresserActiveColumns,
  ] = useActiveColumns(
    "firma-firmaalternativfakturaadresser-active-columns",
    defaultFirmaAltFaktureringsAdresserColumns,
  );

  const { data, loading, error, refetch } = useQuery<{
    firma: {
      firmaNr: number;
      firmaBetalingsadresse: FirmaBetalingsadresse[];
    };
  }>(GET_FIRMA_BY_ID_BETALINGSADRESSE(), {
    variables: {
      id: firmaId,
    },
  });
  if (error) {
    return <div>Fejl</div>;
  }
  if (loading) return <Spinner />;
  if (!data?.firma.firmaBetalingsadresse || !firmaId) return null;
  const firmaBetalingsadresse = data.firma.firmaBetalingsadresse;

  // event handlers
  function downloadHandler() {
    if (firmaBetalingsadresse?.length) {
      tableToXLSX(firmaAltFaktureringsAdresser, firmaBetalingsadresse, {
        fileName: `alternative-faktureringsadresser-for-${data?.firma.firmaNr}`,
        additionalData: {
          Firmanummer: data?.firma.firmaNr,
        },
      });
    }
  }

  return (
    <AccordionCard
      title="Alternative faktureringsadresser"
      value="alternative-faktureringsadresser"
      localstorageKey="firma-id-stamoplysninger-accordion-value"
      isEmpty={!data.firma?.firmaBetalingsadresse?.length}
    >
      {firmaBetalingsadresse.length === 0 ? (
        <IngenRegistreringer
          invalidateData={refetch}
          editConfig={canWrite ? firmaAltAdresseForm(firmaId) : undefined}
        />
      ) : (
        <ModalProvider>
          <ButtonDownload
            onClick={downloadHandler}
            isDisabled={!firmaBetalingsadresse?.length}
          />
          <TableRoot>
            <TableHead ColumnEditorTrigger={createColumnEditorTrigger()}>
              <>
                <th className="indicator"></th>
                {getHeaderActiveColumns(firmaAltFaktureringsAdresser, {
                  defaultSortBy,
                  setSortBy,
                  sortBy,
                })}
              </>
            </TableHead>
            <TableBody>
              {orderBy(
                firmaBetalingsadresse,
                sortBy.map((s) => s.field),
                sortBy.map((s) => s.direction),
              ).map((adr, i) => {
                return (
                  <TableRow
                    key={i}
                    invalidateData={refetch}
                    editConfig={
                      canWrite
                        ? firmaAltAdresseForm(firmaId, adr.id, adr.adresse)
                        : undefined
                    }
                  >
                    <td
                      className={`indicator${
                        periodeIsActive(
                          new Date(adr.periode.fraDato),
                          new Date(adr.periode.tilDatoInklusiv),
                        )
                          ? " has-effect"
                          : ""
                      }`}
                    >
                      <ActiveIcon />
                    </td>
                    <>
                      {firmaAltFaktureringsAdresser
                        .filter((col) => col.active)
                        .map((col) => {
                          return <TD key={col.field} col={col} rowData={adr} />;
                        })}
                    </>
                  </TableRow>
                );
              })}
            </TableBody>
          </TableRoot>
          <ColumnEditorModal
            defaultColumns={defaultFirmaAltFaktureringsAdresserColumns}
            columns={firmaAltFaktureringsAdresser}
            setActiveColumns={setFirmaAltFaktureringsAdresserActiveColumns}
          />
          <OpretNyButtonBlock
            invalidateData={refetch}
            editConfig={canWrite ? firmaAltAdresseForm(firmaId) : undefined}
          />
        </ModalProvider>
      )}
    </AccordionCard>
  );
}

const defaultSortBy: SortBy[] = [
  {
    direction: "desc",
    field: "fraDato",
  },
];
